// Pure row filtering and sorting for the Tasks grid.
//
// Extracted verbatim from TasksPage. These were closures over component state;
// they now take the same values as explicit arguments. No logic changed.
import { isCorporate } from "./constants";

// Which Status-filter checkbox a row answers to. A finished task answers to
// "completed" whatever status it was left in -- finished is the same rule the
// grid uses everywhere (is_completed OR a "Completed" status).
export function statusKeyOf(row, isFinished) {
  if (isFinished(row)) return "completed";
  return row.status == null ? "none" : String(row.status);
}

// 1-12 from a "YYYY-MM-DD" year_end_date, or null. Read from the string:
// new Date("2026-07-01") is UTC midnight, which is June 30 in Saskatchewan.
export function yearEndMonthOf(row) {
  const m = /^\d{4}-(\d{2})/.exec(row.year_end_date || "");
  return m ? Number(m[1]) : null;
}

// Every filter EXCEPT the activeTab category. Used both as the base for the
// visible rows AND for the tab counts, so each tab shows the count it would
// yield if selected.
export function filterRowsPreTab(rows, {
  query,
  filters,
  personFilter,
  clientStatusFilterSet,
  // Whether a row counts as finished. Injected rather than computed here
  // because the rule needs the loaded status options: a row is finished if
  // `is_completed` is set OR its status is one of the "Completed" ids.
  isFinished = () => false,
  // Status keys the toolbar's Status filter has unticked: a general status id
  // as a string, "none" for a task with no status, or "completed" for every
  // finished task (see statusKeyOf above).
  hiddenStatuses = new Set(),
}) {
  return rows.filter((c) => {
    if (query.trim()) {
      const q = query.toLowerCase();
      const ownerNames = String(c.assigned_employee_names || "");
      if (
        !c.name?.toLowerCase().includes(q) &&
        !c.client_name?.toLowerCase().includes(q) &&
        !c.group_name?.toLowerCase().includes(q) &&
        !ownerNames.toLowerCase().includes(q)
      ) {
        return false;
      }
    }

    if (filters.group) {
      const clientGroup = c.group_name || "";
      if (clientGroup !== filters.group) return false;
    }
    if (filters.owner) {
      const ownerId = parseInt(filters.owner, 10);
      // Owner data lives on `assigned_employees` (array of {id, full_name}
      // after normalization in the loader). The legacy `c.owner` field is
      // not present on clients returned by the current serializer, so we
      // must read from `assigned_employees`. Fall back to `c.owner` only
      // for safety in case any code path bypasses normalization.
      const ownerIds = [
        ...((c.assigned_employees || []).map((e) =>
          parseInt(typeof e === "object" ? e.id : e, 10)
        )),
        ...(Array.isArray(c.owner)
          ? c.owner.map((o) => parseInt(o, 10))
          : c.owner != null
          ? [parseInt(c.owner, 10)]
          : []),
      ].filter((n) => !Number.isNaN(n));
      if (!ownerIds.includes(ownerId)) return false;
    }
    if (hiddenStatuses.size && hiddenStatuses.has(statusKeyOf(c, isFinished))) {
      return false;
    }
    // Priority is a boolean flag now, not a High/Medium/Low FK. The filter is
    // "" (any) / "flagged" / "unflagged".
    if (filters.priority === "flagged" && !c.is_flagged) return false;
    if (filters.priority === "unflagged" && c.is_flagged) return false;
    if (filters.type) {
      const wantCorp = filters.type === "Corporate";
      if (isCorporate(c) !== wantCorp) return false;
    }
    // Fiscal year. Not a plain equality test: the chosen year shows in full,
    // and earlier years contribute whatever is still unfinished, so last
    // year's stragglers stay in front of you instead of disappearing when the
    // year rolls. Later years are somebody else's problem until you pick them.
    // A row with no fiscal_year is kept -- nothing can be said about which year
    // it belongs to, and hiding it would make it unreachable from every year.
    if (filters.fiscalYear) {
      const want = parseInt(filters.fiscalYear, 10);
      const fy = c.fiscal_year;
      if (fy != null && fy !== want) {
        if (fy > want) return false;
        if (isFinished(c)) return false;
      }
    }

    if (filters.yearEndMonth) {
      if (yearEndMonthOf(c) !== parseInt(filters.yearEndMonth, 10)) return false;
    }

    // Next year: rolled / waiting / stopped, from TaskSerializer.next_year.
    if (filters.nextYear && c.next_year?.state !== filters.nextYear) return false;

    // Person multi-select filter (toolbar). Checks both `assigned_employees`
    // (array of {id, full_name} objects) AND the legacy `owner` field (which
    // might be a single ID or array of IDs) so it matches whatever shape the
    // backend serializer happens to return.
    if (personFilter.size > 0) {
      const idsFromAssigned = (c.assigned_employees || [])
        .map((e) => (typeof e === "object" ? e.id : e))
        .filter((v) => v !== undefined && v !== null)
        .map((id) => String(id));
      const idsFromOwner = Array.isArray(c.owner)
        ? c.owner.map((o) => String(o))
        : c.owner !== undefined && c.owner !== null
        ? [String(c.owner)]
        : [];
      const allIds = [...idsFromAssigned, ...idsFromOwner];
      const matchesPerson = allIds.some((id) => personFilter.has(id));
      if (!matchesPerson) return false;
    }

    // Client status filter (active / completed / inactive). The Client model's
    // `client_status` field may be missing/empty on legacy rows, in which case
    // we infer the status from `is_active`. This way the default Active+Completed
    // selection still surfaces the rows you'd expect.
    const rawStatus = (c.client_status || "").toString().toLowerCase();
    const inferredStatus = rawStatus
      ? rawStatus
      : c.is_active === false
      ? "inactive"
      : "active";
    if (!clientStatusFilterSet.has(inferredStatus)) return false;

    return true;
  });
}

// The activeTab category filter, applied on top.
//
// /api/tasks/ used to be refetched with ?task_category= on every tab switch.
// It is now fetched once, unfiltered, so this does the category split that the
// server used to do. "other" renders OtherTasksTable instead of these rows, so
// an empty array is fine here.
export function filterRowsByTab(rows, activeTab) {
  return rows.filter((c) => {
    if (activeTab === "personal" && c.task_category !== "personal") return false;
    if (activeTab === "corporate" && c.task_category !== "corporate")
      return false;
    if (activeTab === "tslips" && c.task_category !== "tslips") return false;
    return true;
  });
}

export function sortRows(rows, sortField, sortDirection, metricsByClient) {
  return [...rows].sort((a, b) => {
    if (!sortField) return 0;

    let aVal, bVal;

    // Handle different field types
    switch (sortField) {
      case "name":
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
        break;
      case "group":
        aVal = (a.group_name || "").toLowerCase();
        bVal = (b.group_name || "").toLowerCase();
        break;
      case "owner":
        // Handle assigned_employee_names as array or string
        if (Array.isArray(a.assigned_employee_names)) {
          aVal = a.assigned_employee_names.join(", ").toLowerCase();
        } else {
          aVal = (a.assigned_employee_names || "").toLowerCase();
        }

        if (Array.isArray(b.assigned_employee_names)) {
          bVal = b.assigned_employee_names.join(", ").toLowerCase();
        } else {
          bVal = (b.assigned_employee_names || "").toLowerCase();
        }
        break;
      case "status":
        aVal = a.status || "";
        bVal = b.status || "";
        break;
      case "priority":
        // Flagged rows sort together.
        aVal = a.is_flagged ? 1 : 0;
        bVal = b.is_flagged ? 1 : 0;
        break;
      case "yearEnd":
        aVal = a.year_end_date || "";
        bVal = b.year_end_date || "";
        break;
      case "dueDate":
        aVal = a.due_date || "";
        bVal = b.due_date || "";
        break;
      case "completed":
        // Blank (still open, or finished before the column existed) sorts to
        // the top ascending — "" precedes any ISO date string.
        aVal = a.completed_date || "";
        bVal = b.completed_date || "";
        break;
      case "docsReceived":
        aVal = a.docs_received_date || "";
        bVal = b.docs_received_date || "";
        break;
      case "type":
        aVal = isCorporate(a) ? "Corporate" : "Personal";
        bVal = isCorporate(b) ? "Corporate" : "Personal";
        break;
      case "time":
        aVal = metricsByClient[a.id]?.time || 0;
        bVal = metricsByClient[b.id]?.time || 0;
        break;
      case "invoiced":
        aVal = metricsByClient[a.id]?.invoiced || 0;
        bVal = metricsByClient[b.id]?.invoiced || 0;
        break;
      case "ar":
        aVal = metricsByClient[a.id]?.ar || 0;
        bVal = metricsByClient[b.id]?.ar || 0;
        break;
      // These two compare-and-return directly rather than falling through to
      // the shared comparison below. `default` used to sit above them, which
      // reads as if it makes them unreachable — it does not (a switch only
      // takes `default` when no case matches, wherever it appears), but the
      // layout was misleading enough to get reported as a bug, so it now
      // sits last. Behaviour is unchanged; see the sortRows tests.
      case "fiscal_year":
        return sortDirection === "asc"
          ? (a.fiscal_year || 0) - (b.fiscal_year || 0)
          : (b.fiscal_year || 0) - (a.fiscal_year || 0);

      case "client_status": {
        const statusOrder = { active: 1, completed: 2, inactive: 3 };
        const aOrder = statusOrder[a.client_status] || 99;
        const bOrder = statusOrder[b.client_status] || 99;
        return sortDirection === "asc" ? aOrder - bOrder : bOrder - aOrder;
      }

      default:
        return 0;
    }

    // Compare values
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
}
